import queryString from "query-string";

// Read the handoff_code from the fragment and strip it in one step: the code
// is a single-use credential and must not linger in `location.href` where
// Sentry / analytics / browser history can observe it (F-2026-18388). Only
// the `handoff_code` key is removed — unrelated hash state is preserved.
export const consumeHandoffCode = () => {
  const parsed = queryString.parse(location.hash);
  const raw = parsed.handoff_code;

  // A duplicated `handoff_code` in the fragment (`.toString()` on an array
  // would silently corrupt to a comma-joined value the backend rejects).
  const code = Array.isArray(raw) ? "" : (raw ?? "");

  if (!parsed.handoff_code) {
    return code;
  }

  delete parsed.handoff_code;

  const remaining = queryString.stringify(parsed);
  const newHash = remaining ? `#${remaining}` : "";

  history.replaceState(null, "", location.pathname + location.search + newHash);

  return code;
};

/**
 * Application configuration singleton
 */
class AppConfig {
  private static config: AppConfig | null = null;

  readonly isProduction = import.meta.env.MODE === "production";
  readonly isIframed = window.self !== window.top;
  readonly apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  readonly wsCluster = import.meta.env.VITE_WS_CLUSTER;
  readonly wsKey = import.meta.env.VITE_WS_KEY;
  readonly wsHost = import.meta.env.VITE_WS_HOST;
  readonly wsPort = import.meta.env.VITE_WS_PORT;
  readonly fsOrgId = import.meta.env.VITE_FS_ORG_ID;
  readonly posthogApiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  readonly feedbackChannel = import.meta.env.VITE_SLACK_FEEDBACK_CHANNEL;
  readonly sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  readonly appVersion = import.meta.env.VITE_APP_VERSION;
  readonly isHttps: boolean = this.apiBaseUrl?.startsWith("https") ?? false;

  readonly token: string;
  readonly email: string;
  readonly target: string;
  readonly branding: string;
  readonly emailBranding: string;
  readonly page: string;
  readonly platformTemplateId: string;
  readonly embedWidgetOnBoot: boolean;

  /** Auth attributes */
  readonly secret: string;
  readonly jwt: string;
  readonly handoffCode: string;

  private constructor() {
    const parsed = queryString.parse(location.search);
    this.token = parsed.token?.toString() ?? "";
    this.secret = parsed.secret?.toString() ?? "";
    this.email = parsed.email?.toString() ?? "";
    this.target = parsed.target?.toString() ?? "";
    this.jwt = parsed.jwt?.toString() ?? "";
    this.branding = parsed.branding?.toString() ?? "";
    this.emailBranding = parsed.emailBranding?.toString() ?? "";
    this.page = parsed.page?.toString() ?? "";
    this.platformTemplateId = parsed.borrowerPlatformTemplate?.toString() ?? "";
    this.embedWidgetOnBoot = Boolean(parsed.embedWidgetOnBoot);
    this.handoffCode = consumeHandoffCode();
  }

  public static get instance(): AppConfig {
    if (!this.config) {
      this.config = new AppConfig();
    }
    return this.config;
  }

  public get isEmbedded() {
    return this.isIframed && this.target;
  }

  public get isWidget() {
    return this.isIframed && !this.target;
  }
}

export default AppConfig.instance;
